// L10 Import File Parser
//
// Pure client-side validator for the JSON files produced by Brett's Claude
// L10-transcript-extractor prompt (see /app/memory/system_prompt_l10_extraction.md).
// Contract is authoritative in /app/memory/scope_l10_import_from_file.md §2.
//
// Zero React deps by design — trivially unit-testable and dead-simple.
//
// Return shape:
//   { ok: true, data: { week_of, meeting: {cascade_notes, next_meeting_date}, todos: [...] } }
//   { ok: false, errors: string[] }
//
// Design decisions:
// - Collect ALL validation errors before returning, not just the first — the user
//   should see the full picture, not fix-and-retry ten times.
// - Extra keys are a soft warning (console.warn), not a hard reject — Claude's
//   schema may evolve slightly; we don't want to break on additive fields.
// - `action` empty/missing on a todo is a hard error, not a silent drop — the
//   whole point of importing is human review before write, so telling the user
//   "row 3 is invalid" is more useful than silently losing it.

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isValidYYYYMMDD(s) {
  if (typeof s !== "string" || !DATE_RE.test(s)) return false;
  const d = new Date(s + "T00:00:00Z");
  if (Number.isNaN(d.getTime())) return false;
  // Guard against dates like "2026-02-30" which pass the regex but roll over.
  return d.toISOString().slice(0, 10) === s;
}

const KNOWN_ROOT_KEYS = new Set(["week_of", "meeting", "todos"]);
const KNOWN_MEETING_KEYS = new Set(["cascade_notes", "next_meeting_date"]);
const KNOWN_TODO_KEYS = new Set(["action", "owner", "due_date"]);

export function parseL10ImportFile(raw) {
  // Step 1 — JSON parse.
  let obj;
  try {
    obj = JSON.parse(raw);
  } catch (e) {
    return { ok: false, errors: [`JSON parse error: ${e.message}`] };
  }
  if (obj === null || typeof obj !== "object" || Array.isArray(obj)) {
    return { ok: false, errors: ["Root value must be a JSON object."] };
  }

  const errors = [];

  // Step 2 — root shape.
  if (!("week_of" in obj)) {
    errors.push("`week_of` is required at the root.");
  } else if (!isValidYYYYMMDD(obj.week_of)) {
    errors.push(`\`week_of\` must be a valid YYYY-MM-DD date (got: ${JSON.stringify(obj.week_of)}).`);
  }

  if (!("meeting" in obj) || obj.meeting === null || typeof obj.meeting !== "object" || Array.isArray(obj.meeting)) {
    errors.push("`meeting` must be an object with `cascade_notes` and `next_meeting_date`.");
  } else {
    if (!("cascade_notes" in obj.meeting)) {
      errors.push("`meeting.cascade_notes` key is required (may be empty string).");
    } else if (typeof obj.meeting.cascade_notes !== "string") {
      errors.push(`\`meeting.cascade_notes\` must be a string (got: ${typeof obj.meeting.cascade_notes}).`);
    }
    if (!("next_meeting_date" in obj.meeting)) {
      errors.push("`meeting.next_meeting_date` key is required (may be null).");
    } else if (obj.meeting.next_meeting_date !== null && !isValidYYYYMMDD(obj.meeting.next_meeting_date)) {
      errors.push(`\`meeting.next_meeting_date\` must be YYYY-MM-DD or null (got: ${JSON.stringify(obj.meeting.next_meeting_date)}).`);
    }
    // Soft-warn on unknown keys.
    for (const k of Object.keys(obj.meeting)) {
      if (!KNOWN_MEETING_KEYS.has(k)) {
        // eslint-disable-next-line no-console
        console.warn(`[l10Import] Ignoring unknown meeting field: ${k}`);
      }
    }
  }

  if (!("todos" in obj)) {
    errors.push("`todos` is required (may be an empty array).");
  } else if (!Array.isArray(obj.todos)) {
    errors.push("`todos` must be an array.");
  } else {
    obj.todos.forEach((t, i) => {
      if (t === null || typeof t !== "object" || Array.isArray(t)) {
        errors.push(`\`todos[${i}]\` must be an object.`);
        return;
      }
      if (!("action" in t) || typeof t.action !== "string" || t.action.trim() === "") {
        errors.push(`\`todos[${i}].action\` is required and must be a non-empty string.`);
      }
      if (!("owner" in t)) {
        errors.push(`\`todos[${i}].owner\` key is required (string or null).`);
      } else if (t.owner !== null && typeof t.owner !== "string") {
        errors.push(`\`todos[${i}].owner\` must be a string or null (got: ${typeof t.owner}).`);
      }
      if (!("due_date" in t)) {
        errors.push(`\`todos[${i}].due_date\` key is required (YYYY-MM-DD or null).`);
      } else if (t.due_date !== null && !isValidYYYYMMDD(t.due_date)) {
        errors.push(`\`todos[${i}].due_date\` must be YYYY-MM-DD or null (got: ${JSON.stringify(t.due_date)}).`);
      }
      for (const k of Object.keys(t)) {
        if (!KNOWN_TODO_KEYS.has(k)) {
          // eslint-disable-next-line no-console
          console.warn(`[l10Import] Ignoring unknown todos[${i}] field: ${k}`);
        }
      }
    });
  }

  // Soft-warn on unknown root keys.
  for (const k of Object.keys(obj)) {
    if (!KNOWN_ROOT_KEYS.has(k)) {
      // eslint-disable-next-line no-console
      console.warn(`[l10Import] Ignoring unknown root field: ${k}`);
    }
  }

  if (errors.length > 0) {
    return { ok: false, errors };
  }

  // Step 3 — build a normalized data shape (only known fields, trimmed).
  return {
    ok: true,
    data: {
      week_of: obj.week_of,
      meeting: {
        cascade_notes: obj.meeting.cascade_notes,
        next_meeting_date: obj.meeting.next_meeting_date,
      },
      todos: obj.todos.map((t) => ({
        action: t.action.trim(),
        owner: t.owner === "" ? null : t.owner, // treat empty-string owner as null for consistency
        due_date: t.due_date,
      })),
    },
  };
}
